// What the "Only when…" section SAYS (custom + conditional alarms, phase 6).
// `customAlarmForm.ts` decides what a condition IS and whether it holds; this
// module turns those answers into the operator's words, so every string the
// condition builder shows is pure, testable, and written once:
//
//   - the operator options ("is above" / "is below" / "is on" / "is off");
//   - the per-row live chip ("true now" / "false now" / "no reading");
//   - the permanent fail-closed note;
//   - the review panel's combined live line, including WHY the alarm would stay
//     quiet or why the answer cannot be reached.
//
// The honesty rules are the ones the evaluator forces on us:
//
//   - A clause with no usable reading is `null` HERE (we cannot say) but reads
//     FALSE on the boat (fail-closed). Those are different claims, so the chip
//     says "no reading" and the permanent note explains the boat's behaviour —
//     neither pretends to be the other.
//   - The combined line never guesses. It renders "would stay quiet" only for a
//     definite no (the trigger is not met, or a condition is definitely false),
//     "can’t be decided" only when a NAMED condition is the unknown, and nothing
//     at all when the trigger itself has no reading to judge — the builder's
//     quiet note already covers that case.

import { formatThreshold } from './alarmSentence';
import {
  foldConditions,
  conditionField,
  conditionIndexOf,
  type ConditionDraft,
  type ConditionLogic,
  type ConditionOperator,
  type CustomAlarmField,
} from './customAlarmForm';

/** The operator select's option text — a full phrase, read as "<signal> is above". */
export function conditionOperatorLabel(operator: ConditionOperator): string {
  switch (operator) {
    case 'above':
      return 'is above';
    case 'below':
      return 'is below';
    case 'on':
      return 'is on';
    case 'off':
      return 'is off';
  }
}

/**
 * What a row ASKS FOR, in the words the review line reuses — "above 500 rpm",
 * "on", "off". `undefined` while the row is unfinished, so a half-typed clause
 * never appears in prose as though it were authored.
 */
export function clauseExpectation(row: ConditionDraft, unit?: string | null): string | undefined {
  switch (row.operator) {
    case 'above':
    case 'below': {
      const raw = (row.value ?? '').trim();
      if (raw.length === 0) return undefined;
      const value = Number(raw);
      if (!Number.isFinite(value)) return undefined;
      return `${row.operator} ${formatThreshold(value, unit)}`;
    }
    case 'on':
      return 'on';
    case 'off':
      return 'off';
    default:
      return undefined;
  }
}

/** Chip tone: met, not met, or no reading to judge from. */
export type ClauseChipTone = 'yes' | 'no' | 'unknown';

export interface ClauseChip {
  readonly text: string;
  readonly tone: ClauseChipTone;
  /** The longer form, for the chip's title and its screen-reader label. */
  readonly detail: string;
}

/**
 * The live chip for one condition row. Three-way on purpose: "false now" is a
 * reading that fails the test, "no reading" is no reading — collapsing them
 * would hide the exact case the fail-closed note exists to warn about.
 */
export function clauseChip(verdict: boolean | null): ClauseChip {
  if (verdict === true) {
    return { text: 'true now', tone: 'yes', detail: 'This condition is met right now.' };
  }
  if (verdict === false) {
    return { text: 'false now', tone: 'no', detail: 'This condition is not met right now.' };
  }
  return {
    text: 'no reading',
    tone: 'unknown',
    detail:
      'Nothing recent to judge this condition on. Your boat treats a condition with no reading as not met.',
  };
}

/**
 * The permanent honesty note under the rows. Names the ONE signal when there is
 * one row, because "if Engine 1 revolutions isn’t reporting" is the sentence an
 * operator can act on; goes generic for several, where naming one would imply
 * the others are safe.
 *
 * The `any` wording is different because the consequence is different: with
 * "any one of these is enough", a silent signal only removes one way for the
 * alarm to fire, so claiming it silences the alarm outright would be a lie.
 */
export function failClosedNote(
  labels: readonly (string | undefined)[],
  logic: ConditionLogic
): string {
  const only = labels.length === 1 ? (labels[0] ?? '').trim() : '';
  const subject =
    only.length > 0 ? `If ${only} isn’t reporting` : 'If a condition’s signal isn’t reporting';
  if (logic === 'any' && labels.length > 1) {
    return `${subject}, that condition reads as not met — so this alarm fires only if one of the others is true. A broken sensor can silence it.`;
  }
  return `${subject}, that condition reads as not met — so this alarm won’t fire. A broken sensor silences it.`;
}

// --- the combined live line ------------------------------------------------

/** One condition row as the review line needs it. */
export interface ClauseState {
  /** The signal's human name — "Engine 1 revolutions". */
  readonly label: string;
  /** `clauseHolds` for this row: true / false / cannot say. */
  readonly verdict: boolean | null;
  /** {@link clauseExpectation} for this row — "above 500 rpm". */
  readonly expectation?: string;
  /** The row signal's reading right now — "420 rpm", "On". */
  readonly readingText?: string | null;
}

export interface LiveVerdictInput {
  /** `wouldFireNow` for the trigger: true / false / cannot say. */
  readonly trigger: boolean | null;
  /** The trigger signal's reading — "12.4 V" — when there is one. */
  readonly triggerReadingText?: string | null;
  readonly clauses: readonly ClauseState[];
  readonly logic: ConditionLogic;
}

/** The review line, split so the caller can bold the claim itself. */
export interface LiveVerdictLine {
  /** Everything before the claim: "With the current readings, this alarm". */
  readonly lead: string;
  /** The claim: "would be firing" | "would stay quiet" | "can’t be decided". */
  readonly verdict: string;
  /** Why, when a condition is what decided it. Absent when the trigger alone did. */
  readonly reason?: string;
  readonly tone: 'firing' | 'quiet' | 'unknown';
}

/** "Engine 1 revolutions is 420 rpm, not above 500 rpm". */
function clauseFailure(clause: ClauseState): string {
  const label = clause.label.trim().length > 0 ? clause.label.trim() : 'that signal';
  const expectation = clause.expectation?.trim() ?? '';
  const reading = clause.readingText?.trim() ?? '';
  if (expectation.length === 0) return `${label} does not meet its condition`;
  if (reading.length === 0) return `${label} is not ${expectation}`;
  return `${label} is ${reading}, not ${expectation}`;
}

function clauseLabel(clause: ClauseState): string {
  return clause.label.trim().length > 0 ? clause.label.trim() : 'that signal';
}

/**
 * Why the conditions hold the alarm quiet. Under `all` the FIRST false clause is
 * enough to name — it alone is doing the blocking. Under `any` a false verdict
 * means every clause failed, so naming one would read as though the others were
 * fine.
 */
function quietReason(clauses: readonly ClauseState[], logic: ConditionLogic): string | undefined {
  const failed = clauses.filter((c) => c.verdict === false);
  const first = failed[0];
  if (first === undefined) return undefined;
  if (logic === 'any' && failed.length > 1) return 'none of these conditions are met';
  return clauseFailure(first);
}

/** "no reading for shore power" — the first clause we cannot judge. */
function unknownReason(clauses: readonly ClauseState[]): string | undefined {
  const unknown = clauses.find((c) => c.verdict === null);
  return unknown === undefined ? undefined : `no reading for ${clauseLabel(unknown)}`;
}

/**
 * The lead-in. The trigger's own reading is quoted only when it is the whole
 * story — with conditions on screen the line is about several signals at once,
 * and quoting one of them would read as if it were the only one that mattered.
 */
function leadFor(input: LiveVerdictInput): string {
  const reading = (input.triggerReadingText ?? '').trim();
  if (input.clauses.length === 0 && reading.length > 0) {
    return `With the current reading (${reading}), this alarm`;
  }
  return 'With the current readings, this alarm';
}

/**
 * The review panel's live line, or `null` when there is nothing honest to say.
 *
 * Mirrors `combinedWouldFire` exactly — a definite `false` on either leg wins,
 * and only an unattributable unknown produces no line:
 *
 *   - trigger false ⇒ quiet, no reason (the lead already quotes the reading);
 *   - a condition false ⇒ quiet, naming it, EVEN IF the trigger is unknown —
 *     the alarm cannot fire either way, and that is worth saying;
 *   - trigger unknown and nothing false ⇒ no line. The builder's "last reported
 *     …" note covers a quiet trigger signal; repeating it here as a verdict
 *     would dress up "we have no idea" as an evaluation.
 *   - trigger met, a condition unknown ⇒ "can’t be decided", naming it.
 */
export function liveVerdictLine(input: LiveVerdictInput): LiveVerdictLine | null {
  const { trigger, clauses, logic } = input;
  const lead = leadFor(input);
  const folded = foldConditions(
    clauses.map((c) => c.verdict),
    logic
  );

  if (trigger === false) return { lead, verdict: 'would stay quiet', tone: 'quiet' };
  if (folded === false) {
    return {
      lead,
      verdict: 'would stay quiet',
      tone: 'quiet',
      reason: quietReason(clauses, logic),
    };
  }
  if (trigger === null) return null;
  if (folded === null) {
    return { lead, verdict: 'can’t be decided', tone: 'unknown', reason: unknownReason(clauses) };
  }
  return { lead, verdict: 'would be firing', tone: 'firing' };
}

// --- row bookkeeping -------------------------------------------------------

/**
 * The touched-field set after condition row `removed` is deleted. Row errors are
 * keyed by POSITION (`condition:2`), so dropping a row without shifting the keys
 * above it moves every message one row down — the builder would show row 3's
 * complaint beside row 2. Rows below the removed one keep their key; the removed
 * row's is dropped; everything else is left alone.
 */
export function shiftConditionTouched(
  touched: Iterable<CustomAlarmField>,
  removed: number
): Set<CustomAlarmField> {
  const next = new Set<CustomAlarmField>();
  for (const field of touched) {
    const index = conditionIndexOf(field);
    if (index === null) {
      next.add(field);
      continue;
    }
    if (index === removed) continue;
    next.add(index > removed ? conditionField(index - 1) : field);
  }
  return next;
}
